import {BrowserAuthError, BrowserCacheLocation, Configuration, InteractionRequiredAuthError, LogLevel, PublicClientApplication, SilentRequest} from "@azure/msal-browser";
import {getHashCode} from "@pnp/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {AuthProvider, AuthProviderCallback} from "@microsoft/microsoft-graph-client";

const debug = initDebugger('MicrosoftLogin');

/**
 * Tjeneste for å hente brukers microsoft OneDrive.
 * <pre>
 * Merk at vi benytter 'authorization code flow' (også kalt 'auth code flow'),
 * se https://learn.microsoft.com/nb-no/entra/identity-platform/migrate-spa-implicit-to-auth-code.
 *
 * Js biblioteket '@azure/msal-browser' (Microsoft Authentication Library for JavaScript (MSAL.js) 2.0 for Browser-Based Single-Page Applications)
 * brukes for å implementere dette.
 *
 * Ved problemer knyttet til tilganger kan tokens benyttet i requester inspiseres ved å kopiere token
 * fra request header og limes inn i https://jwt.ms for dekoding.
 *
 * DEFINISJON AV TILGANGER
 * Scope angitt som parameter til autentiseringsmetoden angir hvilke tilganger koden ber om.
 * Normalt vil formen '<api url>/.default' benyttes, noe som fører til at tilgangene vi ber om er de som er
 * definert i OneDriveFilePicker i styreplan-infrastruktur prosjektet.
 * Les mer om .default scopet på https://learn.microsoft.com/en-us/entra/identity-platform/scopes-oidc#the-default-scope
 *
 * OPPSETT AV CONSENT
 * Når bruker Logger inn må vedkommende godta tilgangene vi spør om. Dette gjøres via en egen 'Consent' dialog.
 * Hvorvidt bruker har rettigheter til å godta tilgangene avhenger av hvordan dette er satt opp i brukers
 * Entra Id (Ad) instans i brukers Azure/Office365 instans.
 * Ler mer om oppsett av dette på https://learn.microsoft.com/nb-no/entra/identity/enterprise-apps/configure-user-consent?pivots=portal
 *
 * HVORDAN TVING FREMVISNING AV CONSENT DIALOG PÅ NYTT
 * Ved behov for å logge helt ut av office 365/microsoft kan du åpne
 * URLen https://login.microsoftonline.com/common/v2.0/.well-known/openid-configuration i nettleseren og
 * slette alle cookies (bruker denne URLen da denne ikke fører til noen redirect ved åpning)
 * Gjør det samme for styreplan-adressen (feks http://localhost:8888 ifbm utvikling).

 * Ved førstegangs innlogging i Microsoft vises en "consent screen" der bruker må bekrefte at de rollene
 * vi spør om (se OneDriveFilePicker i styreplan-infrastruktur prosjektet) tillates.
 *
 * For å tvinge fremvisning av denne consent screen på nytt må applikasjonen slettes fra azure (under den tenant bruker som
 * prøver å logge inn er registrert i) under Entra ID > manage > Enterprise Applications > 'navn på filepciker applikasjonen' > properties,
 * klikk her på 'delete'.
 *
 * TESTING
 * Ved opplasting av dokumenter fra OneDrive i utviklingsmiljøet må vår statiske klokke resettes til å bruke reel klokke,
 * ellers vil man få feil av typen 'GraphError: Lifetime validation failed, the token is expired.'
 * Dette gjøres ved å midlertidig kommentere ut innholdet i metoden 'setMockDateTilJavaNå' i lederplan-klient/src/scripts/felles/test/mocks/mockDate.ts
 * </pre>
 */
export class MicrosoftLogin {

    private static msalParams(clientId: string, redirectUrl: string): Configuration {
        return {
            auth: {
                authority: 'https://login.microsoftonline.com/common',
                clientId: clientId,
                redirectUri: redirectUrl
            },
            cache: {cacheLocation: BrowserCacheLocation.LocalStorage},
            system: {
                loggerOptions: {
                    //logLevel: LogLevel.Trace,
                    loggerCallback: (level, message, containsPii) => {
                        debug(`MSAL [${level}]: ${message}`)
                    }
                }
            }
        }
    }

    private app: PublicClientApplication;

    constructor(clientId: string, redirectUrl: string) {
        debug('konstruktør', MicrosoftLogin.msalParams(clientId, redirectUrl))
        this.app = new PublicClientApplication(MicrosoftLogin.msalParams(clientId, redirectUrl));
    }

    authProvider(requiredPermissions: string[]): AuthProvider {
        return async (callback: AuthProviderCallback) => {
            try {
                callback(undefined, await this.hentTokenMedScope(requiredPermissions));
            } catch (e) {
                debug('authProvider: FEIL', e)
                callback(e, null);
            }
        }
    }

    async hentTokenMedScope(scopes: string[], additionalAuthParams?: Omit<SilentRequest, "scopes">): Promise<string> {
        debug('hentTokenMedScope', scopes);
        await this.app.initialize()

        let accessToken: string | null = null;
        const authParams = {scopes, ...additionalAuthParams};

        try {
            accessToken = await this.hentEksisterendeToken(authParams);

        } catch (e) {
            debug(`hentTokenMedScope hentEksisterendeToken kastet exeption`, e);
            accessToken = await this.loginOgHentToken(authParams);
            if (accessToken == null) throw e;
        }

        debug(`hentTokenMedScope returnerer token. ${getHashCode(accessToken)}`);
        //debug(accessToken);
        return accessToken;
    }

    public async hentEksisterendeToken(authParams: SilentRequest) {
        debug(`hentEksisterendeToken`);
        const resp = await this.app.acquireTokenSilent(authParams!);
        debug(`hentEksisterendeToken: app.acquireTokenSilent returnert`, resp);
        return resp.accessToken;
    }

    public async logut() {
        await this.app.logoutPopup();
    }

    static erUserCanceledError(e: any) {
        return e instanceof BrowserAuthError && e.errorCode == 'user_cancelled';
    }

    static erConsentRequiredError(e: any) {
        return e instanceof InteractionRequiredAuthError && e.errorCode == 'consent_required';
    }

    //private hjelpemetoder

    /**
     * Viser popup med autentiserings-gui.
     * <pre>
     * Kaster exception ved feil.
     * Følgende exceptions bør håndteres av kaller:
     * - BrowserAuthError med errorCode == 'user_cancelled' - kaste dersom bruker lukker popup
     * - InteractionRequiredAuthError med errorCode == 'consent_required' - kaste dersom bruker klikker på 'avbryt' i consent-dialogen
     * </pre>
     */
    private async loginOgHentToken(authParams: SilentRequest): Promise<string | null> {
        debug(`loginOgHentToken`, authParams);
        const resp = await this.app.loginPopup(authParams);
        debug(`loginOgHentToken: app.loginPopup returnert`, resp);
        this.app.setActiveAccount(resp.account);

        return resp.idToken ? await this.hentEksisterendeToken(authParams) : null;
    }

}
